import { Api } from 'api';
import { callApi } from 'root/appSaga';
import { all, put, select, takeEvery } from 'redux-saga/effects';
import * as vehiclesActions from 'features/vehicles/vehiclesActions';
import * as modalWindowActions from 'root/modal/modalWindowActions';
import * as Sentry from "@sentry/react";
import { generateAllQueryParams } from 'myParkdUtils/utils';
import { Company, SagaGenerator, WorkingWeek } from 'types';
import { getCompany } from 'features/company/companyDetails';


export function* companyVehicleSaga(): SagaGenerator {
    return yield all([
        takeEvery(vehiclesActions.COMPANY_VEHICLES_REQUEST_START, getCompanyVehicles),
        takeEvery(vehiclesActions.COMPANY_VEHICLE_CREATE_START, createCompanyVehicle),
        takeEvery(vehiclesActions.UPDATE_DEVICE_MODE, updateDeviceMode),
        takeEvery(vehiclesActions.UPDATE_LICENSE_PLATE_WORKING_WEEK, updateLicensePlateWorkingWeek)
    ]);
}

export function* getCompanyVehicles(params: ReturnType<typeof vehiclesActions.updateCompanyVehicles>): SagaGenerator {
    try {
        const allQueryParams = generateAllQueryParams(params.payload);
        const companyVehicles = yield callApi(Api.getCompanyVehicles, allQueryParams);
        yield put(vehiclesActions.updateCompanyVehiclesCompleted(companyVehicles));
    } catch (error) {
        yield put(vehiclesActions.updateCompanyVehiclesError(error));
        console.error(error);
    }
}

export function* createCompanyVehicle(params: ReturnType<typeof vehiclesActions.createCompanyVehicle>): SagaGenerator {
    try {
        const vehicle = params.payload.vehicle;
        yield callApi(Api.createCompanyVehicles, vehicle);
        yield put(vehiclesActions.createCompanyVehicleCompleted());
    } catch (error) {
        yield put(vehiclesActions.createCompanyVehicleError(error));
    }
}

export function* updateDeviceMode(params: ReturnType<typeof vehiclesActions.updateDeviceMode>): SagaGenerator {
    try {
        const { localizedLicensePlate, deviceMode } = params.payload
        yield callApi(Api.updateDeviceMode, localizedLicensePlate, deviceMode);
        yield put(vehiclesActions.updateDeviceModeCompleted(localizedLicensePlate, deviceMode));
    } catch (error) {
        yield put(vehiclesActions.updateDeviceModeError(error));
    }
}

export function* updateLicensePlateWorkingWeek(params: ReturnType<typeof vehiclesActions.updateLicensePlateWorkingWeek>): SagaGenerator {
    try {
        const { vehicle, workingWeek } = params.payload;
        const company: Company = yield select(getCompany);
        const newWorkingWeek = workingWeek ? mapMidnightEndTimes(workingWeek) : workingWeek;

        yield callApi(Api.updateLicensePlateWorkingHours, vehicle.localizedLicensePlate, company.publicId, newWorkingWeek);
        yield put(vehiclesActions.updateLicensePlateWorkingWeekCompleted(vehicle, workingWeek));
        yield put(modalWindowActions.closeModal());
    } catch (error) {
        console.error(error);
        Sentry.captureException(error);
        yield put(vehiclesActions.updateLicensePlateWorkingWeekError(error));
    }
}

export const mapMidnightEndTimes = (workingWeek: WorkingWeek): WorkingWeek => {
    let roundedSchedule: Record<string, { start: string; end: string }[]> = {};

    Object.entries(workingWeek?.schedule!).forEach(([weekday, daySchedule]) => {
        if (daySchedule[0]?.end?.startsWith('23:59')) {
            roundedSchedule[weekday] = [{ start: daySchedule[0]?.start, end: '23:59:59' }]
        } else {
            roundedSchedule[weekday] = daySchedule
        }
    });

    return {
        schedule: roundedSchedule,
        version: workingWeek?.version!
    }
}
